import { Banner } from '@components';
import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import { Refresh, Trash } from 'iconsax-react';
import { type FC, useContext } from 'react';
import { Link } from 'react-router-dom';

import { SlabContext } from '@core/context/SlabContext';
import type { ProjectOrQuoteProductFormikType } from '@core/formik/projectOrQuoteProduct';
import type {
  ProductSectionType,
  SharedFormikType,
  TruckingTypeActions,
} from '@core/hooks/useProductSection';
import {
  createValidateTruckingType,
  useProductTruckTypeSection,
} from '@core/hooks/useProductTruckTypeSection';
import Enums from '@core/types/api/Enums';
import type { TruckingTypeSummary } from '@core/types/api/TruckingTypeSummary/TruckingTypeSummary';
import type { List } from '@core/types/list';
import { CurrencyString } from '@core/utils/currency';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { getCurrencySymbol } from '@core/utils/format';
import { getUnitLabelWithSlash } from '@core/utils/unitHelpers';
import { useConfirmDialog } from '@web/providers/ConfirmProvider/useConfirmDialog';
import { lookups } from '@web/utils/DomainHelpers';

import { FieldWithCustomChip } from '../../FieldWithCustomChip/FieldWithCustomChip';
import { Input } from '../../Input/Input';
import { InputDropdown } from '../../InputDropdown/InputDropdown';
import { LookupInput } from '../../LookupInput/LookupInput';
import { PermissionGuard } from '../../Permissions/PermissionGuard';
import { SlabCard } from '../../SlabCard/SlabCard';
import { SlabCardBody } from '../../SlabCard/SlabCardBody';
import { SlabCardHeader } from '../../SlabCard/SlabCardHeader';

const ORPHANED_TRUCKING_TYPE_ERROR =
  'Selected truck type is no longer available for this product. Please select a valid truck type.';

type Props = {
  index: number;
  formikSectionType: ProductSectionType;
  readOnly: boolean;
  actions: TruckingTypeActions;
  isProjectForm: boolean;
  additionalTruckingInfoIndex?: number;
  title?: string;
  onRemove?: () => void;
  truckingTypes: List<TruckingTypeSummary>;
  boxColor?: string;
  currencyCode?: string | null;
};

export const ProductTruckTypeSection: FC<Props> = ({
  index,
  formikSectionType,
  readOnly,
  actions,
  isProjectForm,
  additionalTruckingInfoIndex,
  title,
  onRemove,
  truckingTypes,
  boxColor,
  currencyCode,
}) => {
  const confirm = useConfirmDialog();
  const formikBag = useFormikContext<SharedFormikType>();
  const theme = useTheme();

  const { userInfo } = useContext(SlabContext);

  const productPathPrefix = `${formikSectionType}.${index}`;
  const formikProduct =
    formikBag.getFieldMeta<ProjectOrQuoteProductFormikType>(productPathPrefix).value;
  const isNonTaxableProduct = formikProduct.product.nonTaxable ?? false;

  const handleRemove = (truckingType: TruckingTypeSummary | undefined): void => {
    if (onRemove === undefined) {
      return;
    }
    if (truckingType === undefined) {
      onRemove();
      return;
    }
    confirm.alert({
      title: 'Remove Trucking Type',
      description: `Remove ${truckingType.name} trucking from ${formikProduct?.product.option?.label}?`,
      actions: [
        { label: 'Cancel', onClick: (): void => {}, variant: 'slab-secondary' },
        {
          label: 'Remove',
          onClick: (): void => {
            onRemove();
          },
          variant: 'slab-primary',
        },
      ],
    });
  };

  const canViewTruckingType = userInfo?.hasPermissions([Enums.Permission.ViewTruckingType]);

  const truckingInfoPathPrefix =
    additionalTruckingInfoIndex === undefined
      ? `${formikSectionType}.${index}`
      : `${formikSectionType}.${index}.additionalTruckingInfos.${additionalTruckingInfoIndex}`;

  const {
    isHaulRateChangedManually,
    isMinimumHaulChargeChangedManually,
    isHaulCostChangedManually,
    isTruckingTypeOrphaned,
    availableTruckingTypes,
    selectedTruckingTypeLabel,
    selectedTruckingType,
  } = useProductTruckTypeSection({
    formikBag,
    truckingInfoPathPrefix,
    productPathPrefix,
    truckingTypes,
  });

  const hasTruckingTypesAvailable = truckingTypes && truckingTypes.count > 0;

  const currencySymbol = getCurrencySymbol(currencyCode);

  const truckingTypeLookups = lookups({
    items: availableTruckingTypes,
    label: (tt) => tt.name,
    sublabels: (tt) =>
      hasValue(tt.defaultHaulRate)
        ? [`Default haul rate: ${CurrencyString({ cur: tt.defaultHaulRate, currencyCode })}`]
        : undefined,
    count: availableTruckingTypes.length,
    compare: null,
  });

  const validateTruckingType = createValidateTruckingType(
    truckingTypeLookups,
    ORPHANED_TRUCKING_TYPE_ERROR,
  );

  return (
    <SlabCard
      sx={{
        backgroundColor: boxColor ?? theme.palette.SlabGray[0],
        margin: 0,
      }}
    >
      <SlabCardHeader
        sx={{
          display: 'flex',
          backgroundColor: boxColor ?? theme.palette.SlabGray[0],
          gap: 1,
          paddingY: 1.5,
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.SlabGray.Stroke}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Typography variant='h6/medium'>{title ?? 'Trucking Info'}</Typography>
          {hasStringValue(selectedTruckingTypeLabel) && (
            <Typography variant='h6' sx={{ color: theme.palette.SlabIndigo[700] }}>
              ({selectedTruckingTypeLabel})
            </Typography>
          )}
        </Box>
        {onRemove && !readOnly && (
          <IconButton onClick={() => handleRemove(selectedTruckingType)}>
            <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
          </IconButton>
        )}
      </SlabCardHeader>

      <SlabCardBody>
        {!hasTruckingTypesAvailable && (
          <Banner severity='warning'>
            There are no trucking types for this product.{' '}
            {!canViewTruckingType ? (
              'Please contact a Slabstack administrator for support.'
            ) : (
              <>
                Configure a trucking type in the <Link to='/trucking'>Trucking Types</Link> page.
              </>
            )}
          </Banner>
        )}
        {isTruckingTypeOrphaned && (
          <Banner severity='warning'>
            The previously selected truck type - &quot;{selectedTruckingTypeLabel}&quot; is no
            longer available to use. Select a new truck type or update the existing one.
          </Banner>
        )}

        <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap', mt: 2 }}>
          <LookupInput
            tip={
              isTruckingTypeOrphaned && hasTruckingTypesAvailable
                ? 'Please select a valid truck type.'
                : undefined
            }
            readOnly={readOnly}
            sx={{ flex: 1, flexBasis: 120 }}
            formState={formikBag.values}
            name={`${truckingInfoPathPrefix}.truckingType` as any}
            label='Truck type'
            options={truckingTypeLookups}
            // Add validation for quote products only
            validate={!isProjectForm ? validateTruckingType : undefined}
            onMatchChange={(id): void => {
              if (!hasValue(id)) {
                formikBag.setFieldValue(`${truckingInfoPathPrefix}.haulRate`, null);
                formikBag.setFieldValue(`${truckingInfoPathPrefix}.haulCost`, null);
                formikBag.setFieldValue(`${truckingInfoPathPrefix}.minimumHaulCharge`, null);
                return;
              }
              const summary = truckingTypes.items.find((tt) => tt.id === id);
              if (!hasValue(summary)) {
                return;
              }
              const defaults = {
                haulRate: summary.defaultHaulRate,
                haulCost: summary.defaultHaulCost,
                minimumHaulCharge: summary.minimumHaulCharge ?? null,
              };
              const option = truckingTypeLookups.find((opt) => opt.value === id) ?? null;
              actions.onTruckingTypeChange(defaults, { id, option });
            }}
          />
          <Input
            readOnly={readOnly}
            sx={{ flex: 1, flexBasis: 120 }}
            name={`${truckingInfoPathPrefix}.withDeliveryDisplayOnly.withDeliverySubtotalPrice`}
            label='Subtotal'
            type='text'
            disabled
          />
          <Input
            readOnly={readOnly}
            sx={{ flex: 1, flexBasis: 120 }}
            name={`${truckingInfoPathPrefix}.withDeliveryDisplayOnly.withDeliveryTotalPrice`}
            label='Total price'
            type='text'
            disabled
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            columnGap: 2,
            flexWrap: 'wrap',
            flex: 1,
          }}
        >
          <FieldWithCustomChip
            label={isHaulRateChangedManually ? 'Custom' : 'Default'}
            field={
              <Input
                readOnly={readOnly}
                name={`${truckingInfoPathPrefix}.haulRate`}
                label='Haul rate'
                endAdornment={getUnitLabelWithSlash(selectedTruckingType?.rateUnit)}
                type='currency'
                startAdornment={currencySymbol}
                onInputChange={async (haulRate): Promise<void> => {
                  actions.onHaulRateChange(haulRate);
                }}
              />
            }
            chipBorderColor={
              isHaulRateChangedManually ? theme.palette.SlabYellow['800'] : undefined
            }
            chipTextColor={isHaulRateChangedManually ? theme.palette.SlabYellow['800'] : undefined}
            showChip={selectedTruckingType !== undefined}
          />
          <PermissionGuard permissions={[Enums.Permission.ViewCostData]}>
            <FieldWithCustomChip
              label={isHaulCostChangedManually ? 'Custom' : 'Default'}
              field={
                <Input
                  readOnly={readOnly}
                  name={`${truckingInfoPathPrefix}.haulCost`}
                  label='Haul cost'
                  type='currency'
                  startAdornment={currencySymbol}
                  endAdornment={getUnitLabelWithSlash(selectedTruckingType?.rateUnit)}
                  onInputChange={async (haulCost): Promise<void> => {
                    actions.onHaulCostChange(haulCost);
                  }}
                />
              }
              chipBorderColor={
                isHaulCostChangedManually ? theme.palette.SlabYellow['800'] : undefined
              }
              chipTextColor={
                isHaulCostChangedManually ? theme.palette.SlabYellow['800'] : undefined
              }
              showChip={selectedTruckingType !== undefined}
            />
          </PermissionGuard>
          <FieldWithCustomChip
            label={isMinimumHaulChargeChangedManually ? 'Custom' : 'Default'}
            field={
              <Input
                readOnly={readOnly}
                name={`${truckingInfoPathPrefix}.minimumHaulCharge`}
                label='Min haul charge'
                type='number'
                startAdornment={currencySymbol}
                onInputChange={async (min): Promise<void> => {
                  actions.onMinimumHaulChargeChange(min === '' ? null : Number(min));
                }}
              />
            }
            chipBorderColor={
              isMinimumHaulChargeChangedManually ? theme.palette.SlabYellow['800'] : undefined
            }
            chipTextColor={
              isMinimumHaulChargeChangedManually ? theme.palette.SlabYellow['800'] : undefined
            }
            showChip={selectedTruckingType !== undefined}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            columnGap: 2,
            flexWrap: 'wrap',
            flex: 1,
          }}
        >
          <InputDropdown
            readOnly={readOnly}
            sx={{ flex: 1, flexBasis: 120 }}
            name={`${truckingInfoPathPrefix}.haulTaxable`}
            label='Haul taxable?'
            disabled={isNonTaxableProduct}
            tip={isNonTaxableProduct ? 'Non-taxable product' : undefined}
            options={[
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ]}
            onSelect={async (haulTaxable: boolean): Promise<void> => {
              actions.onHaulTaxableChange(haulTaxable);
            }}
          />
          <Input
            readOnly={readOnly}
            sx={{ flex: 1, flexBasis: 120 }}
            name={`${truckingInfoPathPrefix}.taxRate`}
            label='Tax rate'
            disabled={isNonTaxableProduct}
            tip={isNonTaxableProduct ? 'Non-taxable product' : undefined}
            type='number'
            max='999.99'
            precision={2}
            maxIntegerDigits={3}
            endAdornment='%'
            onInputChange={async (taxRate): Promise<void> => {
              actions.onTaxRateChange(taxRate);
            }}
          />
          <Input
            readOnly={readOnly}
            sx={{ flex: 1, flexBasis: 120 }}
            name={`${truckingInfoPathPrefix}.withDeliveryDisplayOnly.haulMargin`}
            label='Haul margin'
            type='text'
            disabled
          />
        </Box>
        {selectedTruckingType !== undefined &&
          (isHaulRateChangedManually ||
            isMinimumHaulChargeChangedManually ||
            isHaulCostChangedManually) && (
            <Button
              disabled={readOnly}
              onClick={() => {
                actions.onTruckingTypeChange({
                  haulRate: selectedTruckingType.defaultHaulRate,
                  haulCost: selectedTruckingType.defaultHaulCost,
                  minimumHaulCharge: selectedTruckingType.minimumHaulCharge ?? null,
                });
              }}
              sx={{
                alignSelf: 'flex-start',
                color: theme.palette.SlabIndigo[700],
              }}
              variant='text'
            >
              <Refresh />
              Reset To Default
            </Button>
          )}
      </SlabCardBody>
    </SlabCard>
  );
};
